'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { soundboardStorage, SoundboardItem, SoundboardStorage } from '@/utils/soundboardStorage';

interface SoundboardContextType {
  items: SoundboardItem[];
  isExpanded: boolean;
  isLoading: boolean;
  error: string | null;
  addItem: (file: File) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  reorderItems: (items: SoundboardItem[]) => Promise<void>;
  toggleExpanded: () => Promise<void>;
  playItem: (item: SoundboardItem, onPlay: (item: SoundboardItem) => void) => void;
  getStorageStats: () => { totalSize: number; itemCount: number; maxSize: number; maxItems: number };
  clearAll: () => void;
}

const SoundboardContext = createContext<SoundboardContextType | undefined>(undefined);

interface SoundboardManagerProps {
  children: React.ReactNode;
}

export const SoundboardManager: React.FC<SoundboardManagerProps> = ({ children }) => {
  const [storage, setStorage] = useState<SoundboardStorage>({
    items: [],
    isExpanded: false,
    lastUpdated: new Date()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load soundboard data on mount
  useEffect(() => {
    try {
      const loadedStorage = soundboardStorage.loadSoundboard();
      setStorage(loadedStorage);
      setError(null);
    } catch (err) {
      console.error('Error loading soundboard:', err);
      setError('Failed to load soundboard data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save to localStorage whenever storage changes
  useEffect(() => {
    if (!isLoading) {
      try {
        soundboardStorage.saveSoundboard(storage);
        setError(null);
      } catch (err) {
        console.error('Error saving soundboard:', err);
        setError('Failed to save soundboard data');
      }
    }
  }, [storage, isLoading]);

  const addItem = useCallback(async (file: File): Promise<void> => {
    try {
      setError(null);
      
      // Validate file
      if (!file.type.startsWith('audio/')) {
        throw new Error('Please select an audio file');
      }

      if (file.size > 10 * 1024 * 1024) { // 10MB limit per file
        throw new Error('File size must be less than 10MB');
      }

      // Generate unique ID
      const id = `soundboard-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Create audio URL for preview
      const audioUrl = URL.createObjectURL(file);
      
      // Get audio duration if possible
      let duration: number | undefined;
      try {
        const audio = new Audio(audioUrl);
        await new Promise((resolve, reject) => {
          audio.addEventListener('loadedmetadata', () => {
            duration = audio.duration;
            resolve(true);
          });
          audio.addEventListener('error', reject);
          audio.load();
        });
      } catch (err) {
        console.warn('Could not get audio duration:', err);
      }

      // Create soundboard item
      const newItem: SoundboardItem = {
        id,
        name: file.name.replace(/\.[^/.]+$/, ''), // Remove file extension
        file,
        audioUrl,
        order: 0, // This will be set correctly by the storage manager
        createdAt: new Date(),
        fileSize: file.size,
        duration
      };

      // Add to storage
      const updatedStorage = await soundboardStorage.addItem(newItem);
      setStorage(updatedStorage);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add item to soundboard';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const removeItem = useCallback(async (id: string): Promise<void> => {
    try {
      setError(null);
      const updatedStorage = await soundboardStorage.removeItem(id);
      setStorage(updatedStorage);
    } catch (err) {
      console.error('Error removing item:', err);
      setError('Failed to remove item');
    }
  }, []);

  const reorderItems = useCallback(async (items: SoundboardItem[]): Promise<void> => {
    try {
      setError(null);
      const updatedStorage = await soundboardStorage.reorderItems(items);
      setStorage(updatedStorage);
    } catch (err) {
      console.error('Error reordering items:', err);
      setError('Failed to reorder items');
    }
  }, []);

  const toggleExpanded = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const updatedStorage = await soundboardStorage.updateExpandedState(!storage.isExpanded);
      setStorage(updatedStorage);
    } catch (err) {
      console.error('Error toggling expanded state:', err);
      setError('Failed to update panel state');
    }
  }, [storage.isExpanded]);

  const playItem = useCallback((item: SoundboardItem, onPlay: (item: SoundboardItem) => void): void => {
    try {
      setError(null);
      onPlay(item);
    } catch (err) {
      console.error('Error playing item:', err);
      setError('Failed to play audio');
    }
  }, []);

  const getStorageStats = useCallback(() => {
    return soundboardStorage.getStorageStats();
  }, []);

  const clearAll = useCallback((): void => {
    try {
      setError(null);
      soundboardStorage.clearSoundboard();
      setStorage({
        items: [],
        isExpanded: false,
        lastUpdated: new Date()
      });
    } catch (err) {
      console.error('Error clearing soundboard:', err);
      setError('Failed to clear soundboard');
    }
  }, []);

  const contextValue: SoundboardContextType = {
    items: storage.items,
    isExpanded: storage.isExpanded,
    isLoading,
    error,
    addItem,
    removeItem,
    reorderItems,
    toggleExpanded,
    playItem,
    getStorageStats,
    clearAll
  };

  return (
    <SoundboardContext.Provider value={contextValue}>
      {children}
    </SoundboardContext.Provider>
  );
};

export const useSoundboard = (): SoundboardContextType => {
  const context = useContext(SoundboardContext);
  if (context === undefined) {
    throw new Error('useSoundboard must be used within a SoundboardManager');
  }
  return context;
};
